import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import style from "./style.module.css";
import { HeaderSide } from "./headerside";
import { MapWithPoligons } from "../../MapWithPoligons";
import L from "leaflet";
import { EntCropProductionFilter } from "./api/types";
import { Select } from "antd";
import {
  useGetCropProductionAnalyticalDataRepublicQuery,
  useGetCropProductionAnalyticalDataByRegionQuery,
} from "./api";
import { PreloaderSvg } from "../../preloaderSvg/PreloaderSvg";
import { MainTable } from "./mainTable/mainTable";
import { LeftGraph } from "./leftGraph";
import { RegionTable } from "./regionTable/regionTable";
import SelectTerritorialUnitsCode from "../../selects/SelectTerritorialUnitsCode";
import SelectTypesOfAgriculturalCrops from "../../selects/SelectTypesOfAgriculturalCrops";
import { Button } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import SelectMultiProductType from "../../selects/SelectMultiProductType";

export const Heatmapbytypesofproduction = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<EntCropProductionFilter>({
    year: 2024,
    page: 1,
    size: 1000,
    regionId: 2067,
  });

  const [productIds, setProductIds] = useState<number[]>([]); // для SelectMultiProductType

  const [showMainTable, setShowMainTable] = useState(false);
  const [showRegionTable, setShowRegionTable] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [showAllDistricts, setShowAllDistricts] = useState(false);

  const regionsFilter = {
    year: filters.year,
    page: 1,
    size: 1000,
    categoryIds: filters.categoryIds
  };

  const byRegionFilter: EntCropProductionFilter = {
    year: filters.year,
    page: 1,
    size: 1000,
    regionId: showAllDistricts ? undefined : (selectedRegionId || filters.regionId || 2067),
    categoryIds: productIds.length > 0 ? productIds : undefined // используем productIds для byRegion запроса
  };

  const { data: republicData, isLoading: republicLoading } =
    useGetCropProductionAnalyticalDataRepublicQuery(regionsFilter);

  const { data: byRegionData, isLoading: byRegionLoading } =
    useGetCropProductionAnalyticalDataByRegionQuery(byRegionFilter);

  React.useEffect(() => {
    console.log("Main component republicData:", republicData);
    console.log("Main component byRegionData:", byRegionData);
  }, [republicData, byRegionData]);

  const handleYearChange = (year: number | null) => {
    setFilters((prev) => ({
      ...prev,
      year: year || new Date().getFullYear(),
      page: 1,
    }));
  };

  const handleCropCategoryChange = (categoryId: any, option?: any) => {
    const numericCategoryId = typeof categoryId === 'string' ? parseInt(categoryId, 10) : categoryId;
    setFilters((prev) => ({
      ...prev,
      categoryIds: numericCategoryId ? [numericCategoryId] : undefined,
      page: 1,
    }));
  };

  const handleMultiProductTypeChange = (selectedProducts: number[]) => {
    setProductIds(selectedProducts);
  };

  const handleRegionChange = (regionId: string | number, option?: any) => {
    const numericRegionId = typeof regionId === 'string' ? parseInt(regionId, 10) : regionId;


    if (numericRegionId === 0) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.regionId;
        return { ...newFilters, page: 1 };
      });
      setSelectedRegionId(null);
    } else {
      setFilters((prev) => ({
        ...prev,
        regionId: numericRegionId,
        page: 1,
      }));
      setSelectedRegionId(null);
    }
  };

  const handlePolygonClick = (regionId: number) => {
    // Если включен режим показа всех районов, можно кликать только на области
    if (showAllDistricts) {
      // Проверяем, что кликнули на область (REGION), а не на район
      const clickedRegion = republicData?.data?.find((r: any) => r.id === regionId);
      if (clickedRegion) {
        setSelectedRegionId(regionId);
        setShowAllDistricts(false);
      }
      return;
    }

    // Если кликнули на область (REGION), показываем районы этой области
    if (!selectedRegionId) {
      setSelectedRegionId(regionId);
    } else if (selectedRegionId !== regionId) {
      // Если кликнули на другую область, обновляем selectedRegionId
      setSelectedRegionId(regionId);
    } else {
      // Если кликнули на район той же области, обновляем фильтры
      setFilters((prev) => ({
        ...prev,
        regionId,
        page: 1,
      }));
    }
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const customMarkerIcon = L.icon({
    iconUrl: "/import.svg",
    iconSize: [35, 55],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const getColorByAmount = (amount: number, maxAmount: number) => {
    if (amount === 0) return '#e0f7e9';
    const percentage = (amount / maxAmount) * 100;
    if (percentage >= 80) return '#1b5e20';
    if (percentage >= 60) return '#388e3c';
    if (percentage >= 40) return '#66bb6a';
    if (percentage >= 20) return '#a5d6a7';
    return '#c8e6c9';
  };

  const mapData = (() => {
    // Получаем данные регионов из различных возможных структур
    const getRegionData = (data: any): any[] => {
      if (!data) return [];
      if (data?.res?.content && Array.isArray(data.res.content)) return data.res.content;
      if (data?.res?.data && Array.isArray(data.res.data)) return data.res.data;
      if (Array.isArray(data?.res)) return data.res;
      if (Array.isArray(data)) return data;
      return [];
    };

    // Получаем данные республики
    const getRepublicData = (data: any): any[] => {
      if (!data) return [];
      if (data?.data && Array.isArray(data.data)) return data.data;
      if (Array.isArray(data)) return data;
      return [];
    };

    const byRegionDataArray = getRegionData(byRegionData);
    const republicDataArray = getRepublicData(republicData);

    if (showAllDistricts && selectedRegionId) {
      // 🔥 Только районы выбранной области — цветные
      const maxAmount = Math.max(...(byRegionDataArray.map((r: any) => r.totalAmount || 0) || [0]));

      return byRegionDataArray.map((region: any) => {
        const isInSelectedRegion = region.parentId === selectedRegionId;

        return {
          regionId: region.id,
          nameRu: region.nameRu,
          nameKy: region.nameKy,
          amount: region.totalAmount || 0,
          color: isInSelectedRegion
            ? getColorByAmount(region.totalAmount || 0, maxAmount)
            : 'rgba(255, 255, 255, 0)', // прозрачный
        };
      });
    }

    if (showAllDistricts) {
      // 🔥 Все районы (по всей республике)
      const maxAmount = Math.max(...(byRegionDataArray.map((r: any) => r.totalAmount || 0) || [0]));

      return byRegionDataArray.map((region: any) => ({
        regionId: region.id,
        nameRu: region.nameRu,
        nameKy: region.nameKy,
        amount: region.totalAmount || 0,
        color: getColorByAmount(region.totalAmount || 0, maxAmount),
      }));
    }

    if (selectedRegionId) {
      // 🔥 Только выбранная область, остальные районы прозрачные
      const maxAmount = Math.max(...(byRegionDataArray.map((r: any) => r.totalAmount || 0) || [0]));

      return byRegionDataArray.map((region: any) => ({
        regionId: region.id,
        nameRu: region.nameRu,
        nameKy: region.nameKy,
        amount: region.totalAmount || 0,
        color: getColorByAmount(region.totalAmount || 0, maxAmount),
      }));
    }

    // 🔥 Карта по республике
    const maxAmount = Math.max(...(republicDataArray.map((r: any) => r.totalAmount || 0) || [0]));

    return republicDataArray.map((region: any) => ({
      regionId: region.id,
      nameRu: region.nameRu,
      nameKy: region.nameKy,
      amount: region.totalAmount || 0,
      color: getColorByAmount(region.totalAmount || 0, maxAmount),
    }));
  })();


  const mapPolygonTypes = ["REGION", "AREA", "CITY_OF_REPUBLICAN_SIGNIFICANCE"];

  const selectedRegionName = (() => {
    if (!filters.regionId) return '';

    // Получаем данные республики
    const getRepublicData = (data: any): any[] => {
      if (!data) return [];
      if (data?.data && Array.isArray(data.data)) return data.data;
      if (Array.isArray(data)) return data;
      return [];
    };

    const republicDataArray = getRepublicData(republicData);
    const region = republicDataArray.find((r: any) => r.id === filters.regionId);
    return region ? region.nameRu : '';
  })();

  return (
    <div className={style.container}>
      <HeaderSide />

      <div style={{
        display: 'flex',
        gap: 16,
        marginBottom: 20,
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <Button
          type={showMainTable ? "default" : "primary"}
          icon={showMainTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          shape="round"
          size="middle"
          style={{
            boxShadow: showMainTable ? undefined : "0 2px 8px rgba(24, 144, 255, 0.15)",
            transition: "all 0.2s"
          }}
          onClick={() => setShowMainTable((v) => !v)}
        >
          {showMainTable ? "Скрыть таблицу по республике" : "Показать таблицу по республике"}
        </Button>
        <Button
          type={showRegionTable ? "default" : "primary"}
          icon={showRegionTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          shape="round"
          size="middle"
          style={{
            boxShadow: showRegionTable ? undefined : "0 2px 8px rgba(24, 144, 255, 0.15)",
            transition: "all 0.2s"
          }}
          onClick={() => setShowRegionTable((v) => !v)}
        >
          {showRegionTable ? "Скрыть таблицу по регионам" : "Показать таблицу по регионам"}
        </Button>
        {selectedRegionId && !showAllDistricts && (
          <Button
            type="default"
            shape="round"
            size="middle"
            onClick={() => {
              setSelectedRegionId(null);
              setFilters((prev) => ({
                ...prev,
                regionId: 2067,
                page: 1,
              }));
            }}
          >
            Вернуться к областям
          </Button>
        )}
      </div>
      <div className={style.mapFilters}>
      <Select
                style={{ width: 120 }}
                value={filters.year}
                onChange={handleYearChange}
                placeholder={t("Выберите год")}
              >
                {years.map((year) => (
                  <Select.Option key={year} value={year}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
              <div style={{ width: 120 }}>
                <SelectTerritorialUnitsCode
                  type={["REGION", "CITY_OF_REPUBLICAN_SIGNIFICANCE"]}
                  value={filters.regionId}
                  onChange={handleRegionChange}
                />
              </div>
              <div style={{ width: 120 }}>
                <SelectTypesOfAgriculturalCrops
                  value={filters.categoryIds?.[0]}
                  onChange={handleCropCategoryChange}
                />
              </div>
              <div style={{ width: 200 }}>
                <SelectMultiProductType
                  value={productIds}
                  onChange={handleMultiProductTypeChange}
                  placeholder="Выберите продукты"
                />
              </div>
              </div>
      <div className={style.mapContainer}>
        {showMainTable && (
          <div className={style.tableSection}>
            <MainTable data={republicData} isLoading={republicLoading} />
          </div>
        )}
        <div className={style.mapSection} style={{ width: (!showMainTable && !showRegionTable) ? '100%' : undefined, flexGrow: 1 }}>
          <div className={style.mapContent}>
            <div className={style.mapContent}>
              <MapWithPoligons
                latitude={41.5089324}
                longitude={74.724091}
                customIcon={customMarkerIcon}
                type={mapPolygonTypes}
                selectedYear={filters.year}
                extraData={mapData}
                onPolygonClick={handlePolygonClick}
                selectedRegionId={selectedRegionId}
                showAllDistricts={showAllDistricts}
              />
            </div>
          </div>


        </div>
        {showRegionTable && (
          <div className={style.regionTableSection}>
            <RegionTable data={byRegionData} isLoading={byRegionLoading} selectedRegionName={selectedRegionName} />
          </div>
        )}
      </div>

      {(republicLoading || byRegionLoading) && <PreloaderSvg />}

    
    </div>
  );
};
